import { useTranslation } from 'react-i18next';
import {
  PlusWorkingDaysRequest,
  PlusWorkingDaysResponse,
  ServicePriceType,
  ServiceSettings,
  ServiceType,
  ServiceViewDetails,
  VOLUME_MIN_VAL,
  VOLUME_PRECISION,
} from '@lib/api-interface';
import { UsecaseButton } from '@lib/ui-components';
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { RestUsecaseClientService } from '@lib/common-sdk';
import { addDays } from 'date-fns';
import * as Yup from 'yup';
import { getWasteDetailFields } from '../../shared/waste-detail-fields';

interface ClientCollectionRequestPickupCommonServiceDialogProps {
  row: ServiceViewDetails;
  reload: () => void;
}

export const CommonServicePickupDialog = (props: ClientCollectionRequestPickupCommonServiceDialogProps) => {
  const { t } = useTranslation();
  const row = props.row;
  const [nextWorkingDate, setNextWorkingDate] = React.useState<string>('');
  const currentDay = new Date().getUTCDate();
  const plusWorkingDaysQuery = useQuery({
    queryKey: ['plusWorkingDays', currentDay, 1, true],
    queryFn: () =>
      RestUsecaseClientService.post<PlusWorkingDaysRequest, PlusWorkingDaysResponse>('time', 'plus-working-days', {
        days: 1,
        skipMonday: true,
      }),
  });

  React.useEffect(() => {
    setNextWorkingDate(plusWorkingDaysQuery.data?.date || '');
  }, [plusWorkingDaysQuery]);

  const hasPriceType = (date: string, priceType: ServicePriceType): boolean => {
    const setting = row.settings
      ?.sort((s1, s2) => {
        if (s1.activeFromDate === s2.activeFromDate) return 0;
        return s1.activeFromDate! < s2.activeFromDate! ? 1 : -1;
      })
      ?.find((setting: ServiceSettings) => setting.activeFromDate! <= date);
    return setting?.clientCollectionPrice?.priceType === priceType;
  };

  return (
    <UsecaseButton
      key={row.id}
      groupName='collection-request-client'
      useCaseName='createCollectionRequestByClient'
      buttonLabel={t('reportPickup')}
      color={'secondary'}
      variant={'contained'}
      dialogTitle={t('sendPickupRequest')}
      isDisabled={!row.active || (row.serviceType === ServiceType.ADDITIONAL && row.constantCost)}
      fields={[
        {
          label: t('pickupDate'),
          type: 'date',
          field: 'collectionDate',
          defaultValue: nextWorkingDate,
          maxDate: addDays(new Date(), 30),
          minDate: new Date(2020, 0, 1),
        },
        {
          label: t('noContainersForPickup'),
          type: 'numeric',
          field: 'containers',
          numericMinValue: 1,
          defaultValue: row.applicableCollectionInfrastructureService === null ? 1 : undefined,
          helperText:
            row.projectInfrastructureService && !row.collectionInfrastructureService ? t('reportContainersAmountForPickupWithCreatesResourceOnlyInfrastructureWarning') : '',
        },
        {
          label: t('volume'),
          type: 'numeric',
          field: 'volume',
          defaultValue: row.applicableCollectionInfrastructureService === null ? 1 : undefined,
          numericPrecision: VOLUME_PRECISION,
          numericMinValue: VOLUME_MIN_VAL,
          numericMaxValue: row.applicableCollectionInfrastructureService?.volume,
          isVisible: (data: any) => hasPriceType(data.collectionDate, ServicePriceType.M3),
        },
        {
          label: t('comment'),
          type: 'text',
          field: 'comment',
        },
        ...getWasteDetailFields(row, t),
      ]}
      validationSchema={{
        containers: Yup.string()
          .when('collectionDate', {
            is: (collectionDate: string) => hasPriceType(collectionDate, ServicePriceType.UNIT),
            then: () => Yup.string().required(t('fieldRequired')),
          })
          .test('validateContainers', (value, context) => {
            if (!value) return true;
            if (!/^\d+$/.test(value)) {
              return context.createError({
                message: t('noContainersForPickup.matches'),
              });
            }
            if (row.collectionInfrastructureService != null && parseInt(value) > (row.applicableCollectionInfrastructureCount || 0)) {
              return context.createError({
                message: t('noContainersForPickup.exceeded', {
                  maxContainers: row.applicableCollectionInfrastructureCount,
                }),
              });
            }
            return true;
          }),
      }}
      hiddenValues={{
        collectionDate: nextWorkingDate,
        serviceId: row.serviceId,
        type: 'PICKUP',
      }}
      convertNoneToNull={true}
      // backInsteadOfCancelButton={true}
      onSaved={async () => props.reload()}
    />
  );
};
